import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
// import PropTypes from 'prop-types';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import styles from './styles.css';
import { MOTD } from './MOTD';
import { Button } from '../../components/Button/Button';
import { Add } from '@mui/icons-material';
import WidgetSelector from '../../components/WidgetSelector';
import classes from '../../api/classes';
import { stores } from '../../stores';
import { ClickAwayListener, MenuItem } from '@mui/material';
import { MenuList } from '@mui/material';
import { Paper } from '@mui/material';
import { filterUserAvailableTemplates } from '../../components/ReportTemplatesManage/utils';
import _ from 'lodash';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import Box from '@mui/material/Box';
import DragIcon from '@mui/icons-material/DragHandle';
import Report from '../../components/Report';
import { useDashboardWidgets } from './hooks/useDashboardWidgets';
const { ReportTemplate } = classes;

function Dashboard() {
	const [widgets, setWidgets] = useDashboardWidgets();
	const [widgetSelectorOpen, setWidgetSelectorOpen] = useState(false);
	const [availableWidgets, setAvailableWidgets] = useState([]);
	const reportWidgetWrapper = useRef(null);
	const [settingsOpenIndex, setSettingsOpenIndex] = useState(false);
	const [isDragging, setIsDragging] = useState(false);
	const type = 'programmatic';

	useEffect(() => {
		const fetchAvailableWidgets = async () => {
			let templates = await ReportTemplate.fnCache.listAvailable({ isDashboardTemplate: true });
			const debugUnavailability = stores.identity.isGhosting();
			templates = filterUserAvailableTemplates(templates, { debugUnavailability });
			setAvailableWidgets(templates.map((t) => ({ ...t, label: t.settings.title, value: t.id })));
		};
		fetchAvailableWidgets();
	}, []);

	const addWidget = useCallback(async (widget) => {
		const newWidget = {
			component: 'Report',
			componentProperties: {
				...widget.settings,
				isActualReport: false,
			},
			id: Date.now(),
		};
		// The new element does not have its final coords immediately
		// after entering the DOM, probably due to the SVG charts being
		// rerendered.
		setTimeout(() => {
			const el = reportWidgetWrapper.current;
			if (el) {
				el.scrollIntoView({
					block: 'start',
					inline: 'nearest',
					behavior: 'smooth',
				});
			}
		}, 1000);
		const updatedWidgets = {
			...widgets,
			[type]: [
				...widgets[type],
				newWidget,
			],
		};
		setWidgets(updatedWidgets);
	}, [setWidgets, widgets]);

	const onDragEnd = (result) => {
		const { destination, source } = result;
		if (!destination) {
			setIsDragging(true);
		} else if (destination.droppableId === 'selectedDashboards') {
			// Update position
			const [draggedWidget] = widgets[type].splice(source.index, 1);
			widgets[type].splice(destination.index, 0, draggedWidget);
			setWidgets(widgets);
		}
		setIsDragging(false);
	};

	const onSaveWidgetClick = (widget) => {
		const widgetToEdit = widgets[type]
			.find(({ id }) => id === widget.id);
		const newProps = {
			...widgetToEdit.componentProperties,
			...(widget.element ? widget.element.getCurrentProperties() : {}),
		};
		widgetToEdit.componentProperties = _.cloneDeep(newProps);
		const updatedSelectedWidgets = widgets[type]
			.map((d) => (d.id === widgetToEdit.id ? widgetToEdit : d));

		const updatedDashboards = {
			...widgets,
			[type]: updatedSelectedWidgets,
		};
		setWidgets(updatedDashboards);
	};

	const onRemoveWidgetClick = (dashboard) => {
		const selectedWidgets = widgets[type];
		const remainingWidgets = selectedWidgets.filter((d) => d.id !== dashboard.id);
		const updatedWidgets = {
			...widgets,
			[type]: remainingWidgets,
		};
		setWidgets(updatedWidgets);
	};

	const widgetsToRender = widgets?.[type] || [];
	for (const widget of widgetsToRender) {
		widget.ref = React.createRef();
	}

	const isEmpty = widgetsToRender?.length < 1;
	let droppableClassName = null;
	if (isEmpty || isDragging) {
		droppableClassName = styles.dashboardPlaceholderDroppable;
	}

	return (
		<div className="mx-auto max-w-[1120px] min-w-0 w-full">
			<div className="flex justify-between items-center w-full mb-6">
				<span className="text-xl font-medium text-grey-800 tracking-normal">Dashboard</span>
				<div className="flex relative justify-end gap-x-8 gap-y-2 flex-wrap grow">
					<Button
						icon={<Add />}
						onClick={() => { setWidgetSelectorOpen(true); }}
					>
						Add dashboard
					</Button>
					{widgetSelectorOpen && (
						<div className="absolute top-full right-0 mt-4 z-200">
							<ClickAwayListener
								onClickAway={() => {
									setWidgetSelectorOpen(false);
								}}
							>
								<Paper>
									<MenuList
										autoFocusItem
										variant="menu"
									>
										<MenuItem disabled>
											Select a template
										</MenuItem>
										<div className="min-w-[350px]">
											<WidgetSelector
												availableWidgets={availableWidgets}
												onSelect={(widget) => {
													addWidget(widget);
													setWidgetSelectorOpen(false);
												}}
											/>
										</div>
									</MenuList>
								</Paper>
							</ClickAwayListener>
						</div>
					)}
				</div>
			</div>
			<Grid container spacing={3}>
				<MOTD />
				<DragDropContext
					onDragStart={() => setIsDragging(true)}
					onDragEnd={onDragEnd}
				>
					<Grid item xs={12}>
						<Droppable droppableId="selectedDashboards">
							{(provided) => (
								<div ref={provided.innerRef} className={droppableClassName} {...provided.droppableProps}>
									{widgetsToRender.map((widget, i) => (
										<Draggable draggableId={String(widget.id)} index={i} key={widget.id}>
											{(draggableProvided) => (
												<Box
													ref={reportWidgetWrapper}
													mb={3}
													display="flex"
													flexDirection="column"
												>
													<Box
														ref={draggableProvided.innerRef}
														{...draggableProvided.draggableProps}
														sx={{
															userSelect: 'none',
															...draggableProvided.draggableProps.style,
														}}
													>
														{/*
													Widget was created with a 'component' field in order
													for this to be generic.
													However, in reality, only 'Report' currently exists
												*/}
														{widget.component === 'Report' && (
															<Report
																{...widget.componentProperties}
																isAdministrator={stores.identity.isAdministrator()}
																user={() => stores.identity.user()}
																ref={(elm) => {
																	widget.element = elm;
																}}
																warnOldData
																onRemove={() => onRemoveWidgetClick(widget)}
																onSave={() => onSaveWidgetClick(widget)}
																settingsOpen={settingsOpenIndex === i}
																onSettingsOpenToggle={(isOpen) => {
																	setSettingsOpenIndex(isOpen ? i : null);
																}}
																header={(
																	<Box
																		bgcolor="primary.main"
																		color="primary.contrastText"
																		display="flex"
																		justifyContent="center"
																		className={styles.dragHandle}
																		{...draggableProvided.dragHandleProps}
																	>
																		<DragIcon className={styles.dragIcon} />
																	</Box>
																)}
															/>
														)}
													</Box>
												</Box>
											)}
										</Draggable>
									))}
									{isEmpty ? (
										<h2
											className={styles.dashboardPlaceholderText}
										>
											Drag a widget here
										</h2>
									) : provided.placeholder}
								</div>
							)}
						</Droppable>
					</Grid>
				</DragDropContext>
			</Grid>
		</div>
	);
}

export default Dashboard;
